"use client";

import { useCallback, useMemo } from "react";
import { parseAsFloat, parseAsString, useQueryStates } from "nuqs";
import type { LatLngTuple } from "leaflet";

export const mapSearchParsers = {
  lat: parseAsFloat.withDefault(42.69751),
  lng: parseAsFloat.withDefault(23.32415),
  city: parseAsString, // City in format "city--country" (optional)
};

// Pure helper — no hooks, safe to define outside the component
export function createCitySlug(cityName: string): string {
  const parts = cityName.split(",").map((s) => s.trim());
  if (parts.length < 2) return "";

  const citySlug = parts[0]
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^\w-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  const countrySlug = parts[parts.length - 1]
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^\w-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  return `${citySlug}--${countrySlug}`;
}

export function useMapSearchParams() {
  const [params, setParams] = useQueryStates(mapSearchParsers, {
    shallow: false, // Allow browser history navigation (back/forward buttons)
    history: "push", // Push new entries to browser history
  });

  // Memoize coordinates to prevent new array reference on every render
  // This prevents downstream effects from triggering unnecessarily (e.g., map refocusing)
  const coords = useMemo(
    (): LatLngTuple => [params.lng, params.lat],
    [params.lng, params.lat]
  );

  // Parse city from URL param (format: "city--country")
  const city = useMemo(() => {
    if (!params.city) return undefined;

    const parts = params.city.split("--");
    if (parts.length !== 2) return undefined;

    // Convert slugs back to names: "venice--italy" → "Venice, Italy"
    const cityName = parts[0]
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    const countryName = parts[1]
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return `${cityName}, ${countryName}`;
  }, [params.city]);

  // Helper to set coordinates from Leaflet format [lng, lat]
  const setCoords = useCallback(
    (coords: LatLngTuple) => {
      setParams({
        lng: coords[0],
        lat: coords[1],
      });
    },
    [setParams]
  );

  // Helper to set location with coordinates and city name
  const setLocation = useCallback(
    (
      coords: LatLngTuple,
      cityName?: string | null,
      options?: Parameters<typeof setParams>[1]
    ) => {
      setParams(
        {
          lat: coords[1],
          lng: coords[0],
          city: cityName ? createCitySlug(cityName) : null,
        },
        options
      );
    },
    [setParams]
  );

  return {
    coords,
    city,
    lat: params.lat,
    lng: params.lng,
    setCoords,
    setLocation,
    setParams,
  };
}
